import { ca } from '../i18n/ca';
import { useAuth } from './AuthContext';
import { useMeta } from './MetaContext';
import { ProviderButton } from './ProviderButton';

/**
 * The front page at the root host: the mark, the tagline and the doors, and nothing
 * else — no sales copy under them. It replaced a static page that could not know who was signed
 * in; the session cookie is set for the parent domain, so a member who lands here is sent to the
 * platform host by `HostSwitch` and only sees this page's signed-in half when there is nowhere to
 * send them (no `publicBaseUrl`, or it points back at this host). Every link points at the
 * platform host: the app lives there.
 */
export function Landing() {
  const { meta, loaded } = useMeta();
  const { me, loaded: authLoaded } = useAuth();
  const platform = meta.publicBaseUrl || '';
  const panel = meta.panelHost ? `https://${meta.panelHost}/` : null;
  const roles = me.roles ?? [];
  const admin = roles.includes('ADMIN');
  const canPanel = admin || roles.includes('PANEL_ACCESS');
  const providers = meta.features.steam || meta.features.discord;

  return (
    <main className="landing">
      <div className="landing-map" aria-hidden="true" />
      <section className="hero">
        <h1 className="mark" aria-label={meta.name}>
          {meta.name}
        </h1>
        {meta.tagline && (
          <p className="tagline">
            <span className="words">{meta.tagline}</span>
          </p>
        )}
        {loaded && authLoaded && me.authenticated ? (
          <>
            <p className="muted small">{ca.landingSignedIn(me.displayName || me.handle || '')}</p>
            <nav className="actions" aria-label={ca.landingMissions}>
              <a className="btn" href={`${platform}/library`}>
                {ca.landingMissions}
              </a>
              <a className="btn secondary" href={`${platform}/reports`}>
                {ca.landingReports}
              </a>
              {canPanel && panel && (
                <a className="btn secondary" href={panel}>
                  {ca.landingPanel}
                </a>
              )}
              <a className="btn secondary" href={`${platform}/u/${me.handle ?? ''}`}>
                {ca.landingProfile}
              </a>
              {admin && (
                <a className="btn secondary" href={`${platform}/admin`}>
                  {ca.landingAdmin}
                </a>
              )}
              <a className="btn secondary" href={`${platform}/signout`}>
                {ca.signOut}
              </a>
            </nav>
          </>
        ) : (
          <div className="doors">
            <a className="btn" href={`${platform}/signin`}>
              {ca.signIn}
            </a>
            {providers && (
              <>
                <p className="muted small or">{ca.landing.or}</p>
                <div className="providers">
                  {meta.features.steam && (
                    <ProviderButton
                      provider="STEAM"
                      href={`${platform}/api/auth/steam/start`}
                      label={ca.steamSignIn}
                    />
                  )}
                  {meta.features.discord && (
                    <ProviderButton
                      provider="DISCORD"
                      href={`${platform}/api/auth/discord/start`}
                      label={ca.discordSignIn}
                    />
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </section>
    </main>
  );
}
